import { useState } from "react";
import { toast } from "sonner";
import { Star, Heart, Check } from "lucide-react";
import { MENU } from "@/data/content";
import { useReserve } from "@/contexts/ReserveContext";

const itemId = (chapterKey, name) =>
  `${chapterKey}-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

export default function MenuPage() {
  const [active, setActive] = useState(MENU[0].key);
  const chapter = MENU.find((m) => m.key === active);
  const { isInReserve, isInObsession, addToReserve, addToObsession } = useReserve();

  const onAddReserve = (item) => {
    const id = itemId(chapter.key, item.name);
    if (isInReserve(id)) {
      toast("Already in The Reserve");
      return;
    }
    addToReserve({ id, name: item.name, desc: item.desc, price: item.price, chapterTitle: chapter.title });
    toast.success("Added to The Reserve");
  };

  const onSaveObsession = (item) => {
    const id = itemId(chapter.key, item.name);
    if (isInReserve(id)) {
      toast("Already in The Reserve");
      return;
    }
    if (isInObsession(id)) {
      toast("Already on your Obsession List");
      return;
    }
    addToObsession({ id, name: item.name, desc: item.desc, price: item.price, chapterTitle: chapter.title });
    toast.success("Saved to Obsession List");
  };

  return (
    <div data-testid="menu-page" className="pt-28 md:pt-36 pb-24 md:pb-32">
      <section className="mx-auto max-w-[1400px] px-6 md:px-10">
        <p className="label-eyebrow">The Menu — Volume IV</p>
        <h1 className="font-display text-bone text-5xl md:text-7xl mt-5 leading-none">
          A house of slow craft.
        </h1>
        <p className="mt-6 text-bone/65 max-w-2xl">
          Five chapters. Some rotate weekly. Some return only when the ingredient finds us first.
          Espresso is pulled to order. Every dessert is worth the wait.
        </p>

        {/* Category Bar */}
        <div data-testid="menu-categories" className="mt-14 flex flex-wrap gap-2 md:gap-3 border-y border-bone/10 py-5">
          {MENU.map((c) => (
            <button
              key={c.key}
              data-testid={`menu-tab-${c.key}`}
              onClick={() => setActive(c.key)}
              className={`px-4 py-2 text-[11px] uppercase tracking-[0.28em] transition-colors ${
                active === c.key
                  ? "bg-brass text-obsidian"
                  : "text-bone/70 hover:text-brass border border-bone/15 hover:border-brass/50"
              }`}
            >
              {c.title}
            </button>
          ))}
        </div>

        {/* Chapter */}
        <div className="mt-16 grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <p className="label-eyebrow">Chapter</p>
            <h2 className="font-display text-bone text-4xl md:text-5xl mt-4 leading-tight">
              {chapter.title}
            </h2>
            <p className="font-display italic text-bone/65 text-lg mt-4">{chapter.blurb}</p>
            <div className="mt-8 hairline" />
            <p className="mt-6 text-bone/50 text-sm leading-relaxed">
              Prices in USD. Add to The Reserve to secure your selection, or save to your Obsession List for later.
            </p>
          </div>

          <div className="md:col-span-8">
            <ul className="divide-y divide-bone/10" data-testid="menu-items-list">
              {chapter.items.map((item, idx) => {
                const id = itemId(chapter.key, item.name);
                const inReserve = isInReserve(id);
                const inObsession = isInObsession(id);
                return (
                  <li
                    key={item.name}
                    data-testid={`menu-item-${chapter.key}-${idx}`}
                    className="group py-7 grid grid-cols-[1fr_auto] gap-x-6 gap-y-3 hover:bg-bone/[0.02] transition-colors px-2 -mx-2"
                  >
                    <div className="flex flex-col">
                      <div className="flex items-baseline gap-3">
                        <span className="font-display text-bone text-2xl md:text-3xl">{item.name}</span>
                        <span className="text-bone/35 text-[10px] uppercase tracking-[0.3em]">
                          N° {String(idx + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <p className="mt-2 text-bone/60 max-w-xl text-sm md:text-base leading-relaxed">{item.desc}</p>

                      <div className="mt-4 flex flex-wrap gap-2">
                        <button
                          type="button"
                          data-testid={`menu-add-reserve-${chapter.key}-${idx}`}
                          onClick={() => onAddReserve(item)}
                          className={`inline-flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-[0.28em] transition-colors ${
                            inReserve
                              ? "bg-brass/20 text-brass border border-brass/40 cursor-default"
                              : "border border-brass/60 text-brass hover:bg-brass hover:text-obsidian"
                          }`}
                          disabled={inReserve}
                        >
                          {inReserve ? <><Check size={12} /> In The Reserve</> : <><Star size={12} /> Add to The Reserve</>}
                        </button>
                        <button
                          type="button"
                          data-testid={`menu-add-obsession-${chapter.key}-${idx}`}
                          onClick={() => onSaveObsession(item)}
                          className={`inline-flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-[0.28em] transition-colors ${
                            inObsession
                              ? "bg-lavender/15 text-lavender border border-lavender/40 cursor-default"
                              : "border border-bone/25 text-bone/80 hover:text-lavender hover:border-lavender"
                          }`}
                          disabled={inObsession || inReserve}
                        >
                          {inObsession ? <><Check size={12} /> Saved</> : <><Heart size={12} /> Save to Obsession List</>}
                        </button>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="font-display text-brass text-3xl md:text-4xl">${item.price}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
